// Risk management utilities for TradeCopilot

export const getRiskSettings = () => {
  try {
    const data = localStorage.getItem('tradeCopilot_riskSettings');
    return data ? JSON.parse(data) : {
      accountBalance: 10000,
      riskPerTrade: 1.0,
      maxOpenTrades: 3,
      maxDailyLoss: 5.0,
      enabled: true
    };
  } catch (error) {
    console.error('Error reading risk settings:', error);
    return null;
  }
};

export const saveRiskSettings = (settings) => {
  try {
    localStorage.setItem('tradeCopilot_riskSettings', JSON.stringify(settings));
    return settings;
  } catch (error) {
    console.error('Error saving risk settings:', error);
    throw error;
  }
};

export const calculatePositionSize = (accountBalance, riskPerTrade, entryPrice, stopLoss) => {
  if (!accountBalance || !riskPerTrade || !entryPrice || !stopLoss) {
    return null;
  }
  
  const entry = parseFloat(entryPrice);
  const sl = parseFloat(stopLoss);
  const balance = parseFloat(accountBalance);
  const riskPercent = parseFloat(riskPerTrade);
  
  if (isNaN(entry) || isNaN(sl) || isNaN(balance) || isNaN(riskPercent)) {
    return null;
  }
  
  // Calculate risk amount in dollars
  const riskAmount = balance * (riskPercent / 100);
  
  // Calculate pip/point risk
  const pointRisk = Math.abs(entry - sl);
  
  if (pointRisk === 0) return null;
  
  // Position size = Risk Amount / Point Risk
  const positionSize = riskAmount / pointRisk;
  
  return {
    positionSize: positionSize.toFixed(2),
    riskAmount: riskAmount.toFixed(2),
    pointRisk: pointRisk.toFixed(2)
  };
};

export const validateRiskLimits = (riskSettings, todaysTrades = [], newTradeRisk = 0) => {
  if (!riskSettings || !riskSettings.enabled) {
    return { valid: true, warnings: [], violations: [] };
  }
  
  const warnings = [];
  const violations = [];
  
  // Check max open trades
  const openTrades = todaysTrades.filter(t => !t.exitPrice || t.result === null).length;
  if (openTrades >= riskSettings.maxOpenTrades) {
    violations.push(`Max open trades limit reached (${riskSettings.maxOpenTrades})`);
  }
  
  // Calculate daily loss
  const today = new Date().toDateString();
  const todaysLosses = todaysTrades.filter(t => 
    new Date(t.timestamp).toDateString() === today && t.result === 'loss'
  );
  
  const totalDailyLoss = todaysLosses.reduce((sum, t) => {
    const loss = parseFloat(t.riskPercent) || 0;
    return sum + loss;
  }, 0);
  
  const projectedDailyLoss = totalDailyLoss + newTradeRisk;
  
  if (projectedDailyLoss >= riskSettings.maxDailyLoss) {
    violations.push(
      `Daily loss limit would be exceeded (${projectedDailyLoss.toFixed(2)}% / ${riskSettings.maxDailyLoss}%)`
    );
  } else if (projectedDailyLoss >= riskSettings.maxDailyLoss * 0.8) {
    warnings.push(
      `Approaching daily loss limit (${projectedDailyLoss.toFixed(2)}% / ${riskSettings.maxDailyLoss}%)`
    );
  }
  
  // Check if risk per trade exceeds setting
  if (newTradeRisk > riskSettings.riskPerTrade) {
    warnings.push(
      `Trade risk ${newTradeRisk.toFixed(2)}% exceeds recommended ${riskSettings.riskPerTrade}%`
    );
  }
  
  return {
    valid: violations.length === 0,
    warnings,
    violations,
    dailyLoss: totalDailyLoss,
    projectedDailyLoss,
    openTrades
  };
};

export const calculateRiskMetrics = (accountBalance, trades = []) => {
  const today = new Date().toDateString();
  
  // Filter today's trades
  const todaysTrades = trades.filter(t => 
    new Date(t.timestamp).toDateString() === today
  );
  
  // Calculate daily PnL
  let dailyPnL = 0;
  todaysTrades.forEach(trade => {
    if (trade.result === 'win') {
      const reward = parseFloat(trade.rewardPercent) || 0;
      dailyPnL += reward;
    } else if (trade.result === 'loss') {
      const risk = parseFloat(trade.riskPercent) || 0;
      dailyPnL -= risk;
    }
  });
  
  const dailyPnLDollars = (accountBalance * dailyPnL) / 100;
  
  // Count open trades (no result yet)
  const openTrades = trades.filter(t => !t.result).length;
  
  // Calculate wins and losses
  const wins = todaysTrades.filter(t => t.result === 'win').length;
  const losses = todaysTrades.filter(t => t.result === 'loss').length;
  const totalToday = wins + losses;
  const winRate = totalToday > 0 ? (wins / totalToday) * 100 : 0;
  
  return {
    dailyPnL: dailyPnL.toFixed(2),
    dailyPnLDollars: dailyPnLDollars.toFixed(2),
    openTrades,
    todaysTradeCount: todaysTrades.length,
    wins,
    losses,
    winRate: winRate.toFixed(1)
  };
};
